import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'

type SuiteHelpRecord = {
  id: number
  codigo: string
  ordem_servico: string
  assunto: string
  custo: string | null
  prazo: string | null
  quem_pode_solicitar: string | null
  canal_solicitacao: string | null
  quando_abrir: string | null
  sugestao_texto: string | null
  observacao_ia: string | null
  palavras_chave: string[] | null
}

type DraftRelease = {
  id: number
  version: string
  titulo: string
  status: 'rascunho'
}

type DraftChange = {
  id: number
  release_id: number
  operacao: 'INSERT' | 'UPDATE' | 'DELETE'
  record_key: string
  dados_anteriores: Record<string, unknown> | null
  dados_novos: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

type FormState = {
  ordem_servico: string
  assunto: string
  custo: string
  prazo: string
  quem_pode_solicitar: string
  canal_solicitacao: string
  quando_abrir: string
  sugestao_texto: string
  observacao_ia: string
  palavras_chave: string
}

type DraftData = Omit<FormState, 'palavras_chave'> & {
  codigo: string
  palavras_chave: string[]
}

const EMPTY_FORM: FormState = {
  ordem_servico: '',
  assunto: '',
  custo: '',
  prazo: '',
  quem_pode_solicitar: '',
  canal_solicitacao: '',
  quando_abrir: '',
  sugestao_texto: '',
  observacao_ia: '',
  palavras_chave: '',
}

const CSV_HEADERS = [
  'ordem_servico',
  'assunto',
  'custo',
  'prazo',
  'quem_pode_solicitar',
  'canal_solicitacao',
  'quando_abrir',
  'sugestao_texto',
  'observacao_ia',
  'palavras_chave',
] as const

const HEADER_ALIASES: Record<string, keyof FormState> = {
  ordem_servico: 'ordem_servico',
  os: 'ordem_servico',
  ordem_de_servico: 'ordem_servico',
  assunto: 'assunto',
  custo: 'custo',
  prazo: 'prazo',
  quem_pode_solicitar: 'quem_pode_solicitar',
  solicitante: 'quem_pode_solicitar',
  canal_solicitacao: 'canal_solicitacao',
  canal: 'canal_solicitacao',
  quando_abrir: 'quando_abrir',
  sugestao_texto: 'sugestao_texto',
  sugestao_de_texto: 'sugestao_texto',
  observacao_ia: 'observacao_ia',
  observacao: 'observacao_ia',
  palavras_chave: 'palavras_chave',
  palavras: 'palavras_chave',
}

function nextPatchVersion(version: string) {
  const parts = version.split('.').map((part) => Number.parseInt(part, 10))
  if (parts.length >= 3 && parts.every(Number.isFinite)) return `${parts[0]}.${parts[1]}.${parts[2] + 1}`
  return '8.0.1'
}

function normalizeHeader(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/ª|º/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function buildCodigo(ordemServico: string) {
  const value = ordemServico.trim()
  if (!value) return ''
  return value.toLocaleUpperCase('pt-BR').startsWith('OS-') ? value : `OS-${value}`
}

function splitKeywords(value: string) {
  return value.split(/[|,]/).map((item) => item.trim()).filter(Boolean)
}

function normalizeForm(record: SuiteHelpRecord): FormState {
  return {
    ordem_servico: record.ordem_servico ?? '',
    assunto: record.assunto ?? '',
    custo: record.custo ?? '',
    prazo: record.prazo ?? '',
    quem_pode_solicitar: record.quem_pode_solicitar ?? '',
    canal_solicitacao: record.canal_solicitacao ?? '',
    quando_abrir: record.quando_abrir ?? '',
    sugestao_texto: record.sugestao_texto ?? '',
    observacao_ia: record.observacao_ia ?? '',
    palavras_chave: (record.palavras_chave ?? []).join(' | '),
  }
}

function formToDraft(form: FormState): DraftData {
  const ordemServico = form.ordem_servico.trim()
  return {
    codigo: buildCodigo(ordemServico),
    ordem_servico: ordemServico,
    assunto: form.assunto.trim(),
    custo: form.custo.trim(),
    prazo: form.prazo.trim(),
    quem_pode_solicitar: form.quem_pode_solicitar.trim(),
    canal_solicitacao: form.canal_solicitacao.trim(),
    quando_abrir: form.quando_abrir.trim(),
    sugestao_texto: form.sugestao_texto.trim(),
    observacao_ia: form.observacao_ia.trim(),
    palavras_chave: splitKeywords(form.palavras_chave),
  }
}

function recordToDraft(record: SuiteHelpRecord): DraftData {
  return {
    codigo: record.codigo,
    ordem_servico: record.ordem_servico,
    assunto: record.assunto,
    custo: record.custo ?? '',
    prazo: record.prazo ?? '',
    quem_pode_solicitar: record.quem_pode_solicitar ?? '',
    canal_solicitacao: record.canal_solicitacao ?? '',
    quando_abrir: record.quando_abrir ?? '',
    sugestao_texto: record.sugestao_texto ?? '',
    observacao_ia: record.observacao_ia ?? '',
    palavras_chave: record.palavras_chave ?? [],
  }
}

function parseDelimitedText(text: string, delimiter: string) {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    const next = text[index + 1]
    if (char === '"') {
      if (quoted && next === '"') {
        cell += '"'
        index += 1
      } else {
        quoted = !quoted
      }
      continue
    }
    if (char === delimiter && !quoted) {
      row.push(cell)
      cell = ''
      continue
    }
    if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') index += 1
      row.push(cell)
      if (row.some((value) => value.trim())) rows.push(row)
      row = []
      cell = ''
      continue
    }
    cell += char
  }

  row.push(cell)
  if (row.some((value) => value.trim())) rows.push(row)
  return rows
}

function parseBulkCsv(text: string): FormState[] {
  const clean = text.replace(/^\uFEFF/, '').trim()
  if (!clean) throw new Error('O arquivo CSV está vazio.')

  const firstLine = clean.split(/\r?\n/, 1)[0]
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) >= (firstLine.match(/,/g)?.length ?? 0) ? ';' : ','
  const matrix = parseDelimitedText(clean, delimiter)
  if (matrix.length < 2) throw new Error('O CSV precisa ter cabeçalho e pelo menos um registro.')

  const mappedHeaders = matrix[0].map((header) => HEADER_ALIASES[normalizeHeader(header)] ?? null)
  if (!mappedHeaders.includes('ordem_servico')) throw new Error('O CSV precisa conter a coluna “ordem_servico”. Use o modelo disponível no painel.')
  if (!mappedHeaders.includes('assunto')) throw new Error('O CSV precisa conter a coluna “assunto”. Use o modelo disponível no painel.')

  const rows: FormState[] = []
  const seen = new Set<string>()
  matrix.slice(1).forEach((cells, rowIndex) => {
    const row: FormState = { ...EMPTY_FORM }
    mappedHeaders.forEach((field, index) => {
      if (field) row[field] = cells[index]?.trim() ?? ''
    })
    row.ordem_servico = row.ordem_servico.trim()
    row.assunto = row.assunto.trim()
    if (!row.ordem_servico) throw new Error(`Linha ${rowIndex + 2}: ordem de serviço não informada.`)
    if (!row.assunto) throw new Error(`Linha ${rowIndex + 2}: assunto não informado.`)
    const key = buildCodigo(row.ordem_servico).toLocaleLowerCase('pt-BR')
    if (seen.has(key)) throw new Error(`A OS “${row.ordem_servico}” está duplicada no CSV.`)
    seen.add(key)
    rows.push(row)
  })
  return rows
}

async function upsertInChunks(rows: Record<string, unknown>[], chunkSize = 60) {
  for (let index = 0; index < rows.length; index += chunkSize) {
    const result = await supabase
      .from('draft_changes')
      .upsert(rows.slice(index, index + chunkSize), { onConflict: 'release_id,dataset,record_key' })
    if (result.error) throw result.error
  }
}

function csvEscape(value: string) {
  if (!/[;"\r\n]/.test(value)) return value
  return `"${value.replaceAll('"', '""')}"`
}

export function SuiteHelpEditor({ onBack }: { onBack: () => void }) {
  const { profile } = useAuth()
  const [records, setRecords] = useState<SuiteHelpRecord[]>([])
  const [draftRelease, setDraftRelease] = useState<DraftRelease | null>(null)
  const [drafts, setDrafts] = useState<DraftChange[]>([])
  const [search, setSearch] = useState('')
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [editing, setEditing] = useState<SuiteHelpRecord | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [bulkFileName, setBulkFileName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      const [recordsResult, releaseResult] = await Promise.all([
        supabase
          .from('suite_help')
          .select('id,codigo,ordem_servico,assunto,custo,prazo,quem_pode_solicitar,canal_solicitacao,quando_abrir,sugestao_texto,observacao_ia,palavras_chave')
          .order('ordem_servico'),
        supabase
          .from('releases')
          .select('id,version,titulo,status')
          .eq('status', 'rascunho')
          .order('id', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])
      if (recordsResult.error) throw recordsResult.error
      if (releaseResult.error) throw releaseResult.error

      const currentDraft = (releaseResult.data as DraftRelease | null) ?? null
      setRecords((recordsResult.data ?? []) as SuiteHelpRecord[])
      setDraftRelease(currentDraft)

      if (!currentDraft) {
        setDrafts([])
        return
      }

      const draftResult = await supabase
        .from('draft_changes')
        .select('id,release_id,operacao,record_key,dados_anteriores,dados_novos,created_at,updated_at')
        .eq('release_id', currentDraft.id)
        .eq('dataset', 'suite_help')
        .order('id')
      if (draftResult.error) throw draftResult.error
      setDrafts((draftResult.data ?? []) as DraftChange[])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar o editor da Suite Help.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    if (!term) return records
    return records.filter((record) => [
      record.codigo,
      record.ordem_servico,
      record.assunto,
      record.custo,
      record.prazo,
      record.quem_pode_solicitar,
      record.canal_solicitacao,
      record.quando_abrir,
      record.sugestao_texto,
      record.observacao_ia,
      ...(record.palavras_chave ?? []),
    ].some((value) => String(value ?? '').toLocaleLowerCase('pt-BR').includes(term)))
  }, [records, search])

  const pendingByKey = useMemo(() => new Map(drafts.map((draft) => [draft.record_key, draft])), [drafts])

  async function ensureDraftRelease() {
    if (draftRelease) return draftRelease
    if (!profile) throw new Error('Perfil não carregado.')

    const latestResult = await supabase.from('releases').select('version').order('id', { ascending: false }).limit(1).maybeSingle()
    if (latestResult.error) throw latestResult.error
    const version = nextPatchVersion(latestResult.data?.version ?? '8.0.0')
    const createResult = await supabase
      .from('releases')
      .insert({
        version,
        titulo: `Atualização Suite ${version}`,
        resumo: 'Alterações preparadas pelo painel administrativo.',
        status: 'rascunho',
        created_by: profile.id,
      })
      .select('id,version,titulo,status')
      .single()
    if (createResult.error) throw createResult.error
    const created = createResult.data as DraftRelease
    setDraftRelease(created)
    return created
  }

  function openNew() {
    setEditing(null)
    setForm(EMPTY_FORM)
    setFormOpen(true)
    setBulkOpen(false)
    setError('')
    setMessage('')
  }

  function openEdit(record: SuiteHelpRecord) {
    setEditing(record)
    setForm(normalizeForm(record))
    setFormOpen(true)
    setBulkOpen(false)
    setError('')
    setMessage('')
  }

  function closeForm() {
    setFormOpen(false)
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  function updateField(field: keyof FormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function saveDraft(event: FormEvent) {
    event.preventDefault()
    if (!profile) return
    const data = formToDraft(form)
    if (!data.ordem_servico) return setError('Informe a ordem de serviço.')
    if (!data.assunto) return setError('Informe o assunto.')

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      const recordKey = editing?.codigo ?? data.codigo
      const result = await supabase.from('draft_changes').upsert({
        release_id: release.id,
        dataset: 'suite_help',
        operacao: editing ? 'UPDATE' : 'INSERT',
        record_key: recordKey,
        dados_anteriores: editing ? recordToDraft(editing) : null,
        dados_novos: data,
        created_by: profile.id,
        updated_by: profile.id,
      }, { onConflict: 'release_id,dataset,record_key' })
      if (result.error) throw result.error
      closeForm()
      setMessage(`Alteração salva no rascunho ${release.version}. A base oficial ainda não foi modificada.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar o rascunho.')
    } finally {
      setSaving(false)
    }
  }

  async function markDelete(record: SuiteHelpRecord) {
    if (!profile) return
    if (!window.confirm(`Preparar exclusão da OS ${record.ordem_servico} — “${record.assunto}” para a próxima publicação?`)) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      const result = await supabase.from('draft_changes').upsert({
        release_id: release.id,
        dataset: 'suite_help',
        operacao: 'DELETE',
        record_key: record.codigo,
        dados_anteriores: recordToDraft(record),
        dados_novos: null,
        created_by: profile.id,
        updated_by: profile.id,
      }, { onConflict: 'release_id,dataset,record_key' })
      if (result.error) throw result.error
      setMessage(`Exclusão adicionada ao rascunho ${release.version}.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a exclusão.')
    } finally {
      setSaving(false)
    }
  }

  async function markDeleteAll() {
    if (!profile || records.length === 0) return
    const confirmation = window.prompt(
      `Esta ação preparará a exclusão dos ${records.length} registros da Suite Help. Nada será apagado até a publicação.\n\nDigite EXCLUIR ${records.length} para continuar.`,
    )
    if (confirmation !== `EXCLUIR ${records.length}`) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      await upsertInChunks(records.map((record) => ({
        release_id: release.id,
        dataset: 'suite_help',
        operacao: 'DELETE',
        record_key: record.codigo,
        dados_anteriores: recordToDraft(record),
        dados_novos: null,
        created_by: profile.id,
        updated_by: profile.id,
      })))
      setMessage(`${records.length} exclusões foram preparadas no rascunho ${release.version}. A base oficial permanece intacta até publicar.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a exclusão total.')
    } finally {
      setSaving(false)
    }
  }

  function downloadCsvTemplate() {
    const example: FormState = {
      ordem_servico: '999999',
      assunto: 'Exemplo de serviço',
      custo: 'Não Gera Custo',
      prazo: '5 dias úteis',
      quem_pode_solicitar: 'Qualquer pessoa',
      canal_solicitacao: 'Telefone, Whats ou Presencial',
      quando_abrir: 'Descreva quando esta OS deve ser utilizada.',
      sugestao_texto: 'Matrícula: [informar]\nTelefone: [informar]\n\nDescreva a solicitação.',
      observacao_ia: 'Informe limites e situações em que esta OS não deve ser utilizada.',
      palavras_chave: 'exemplo | serviço | orientação',
    }
    const content = `${CSV_HEADERS.join(';')}\n${CSV_HEADERS.map((field) => csvEscape(example[field])).join(';')}\n`
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'modelo_suite_help.csv'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  async function readBulkFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      setBulkText(await file.text())
      setBulkFileName(file.name)
      setError('')
      setMessage(`Arquivo “${file.name}” carregado. Revise e clique em Preparar importação.`)
    } catch {
      setError('Não foi possível ler o arquivo selecionado.')
    }
  }

  async function prepareBulkImport() {
    if (!profile) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const rows = parseBulkCsv(bulkText)
      const release = await ensureDraftRelease()
      const officialByCode = new Map(records.map((record) => [record.codigo.toLocaleLowerCase('pt-BR'), record]))
      await upsertInChunks(rows.map((row) => {
        const data = formToDraft(row)
        const official = officialByCode.get(data.codigo.toLocaleLowerCase('pt-BR'))
        return {
          release_id: release.id,
          dataset: 'suite_help',
          operacao: official ? 'UPDATE' : 'INSERT',
          record_key: official?.codigo ?? data.codigo,
          dados_anteriores: official ? recordToDraft(official) : null,
          dados_novos: data,
          created_by: profile.id,
          updated_by: profile.id,
        }
      }))
      setBulkText('')
      setBulkFileName('')
      setBulkOpen(false)
      setMessage(`${rows.length} registros foram preparados em massa no rascunho ${release.version}. OS existentes viram UPDATE; novas OS viram INSERT.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a importação em massa.')
    } finally {
      setSaving(false)
    }
  }

  async function discardDraft(draft: DraftChange) {
    if (!window.confirm(`Descartar a alteração pendente de “${draft.record_key}”?`)) return
    setSaving(true)
    setError('')
    try {
      const result = await supabase
        .from('draft_changes')
        .delete()
        .eq('id', draft.id)
        .select('id')
        .maybeSingle()
      if (result.error) throw result.error
      if (!result.data) throw new Error('O rascunho não foi removido do banco. Atualize a página e tente novamente.')

      // Remove imediatamente da fonte que alimenta tanto o painel quanto o selo
      // “pendente”, eliminando estados visuais divergentes.
      setDrafts((current) => current.filter((item) => item.id !== draft.id))
      setMessage('Alteração pendente descartada.')

      // Revalida o banco em seguida. Se houver qualquer divergência real, load()
      // reconcilia a interface com a fonte oficial.
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível descartar a alteração.')
    } finally {
      setSaving(false)
    }
  }

  async function publish() {
    if (!draftRelease || profile?.role !== 'administrador' || drafts.length === 0) return
    if (!window.confirm(`Publicar ${drafts.length} alteração(ões) como versão ${draftRelease.version}?`)) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const result = await supabase.rpc('publish_release', { target_release_id: draftRelease.id })
      if (result.error) throw result.error
      setMessage(`Versão ${draftRelease.version} publicada com sucesso. Os operadores já receberão a Suite Help atualizada.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível publicar a atualização.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="workspace-content admin-editor suite-help-editor">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={onBack}>← Voltar ao painel</button>
          <p className="eyebrow">ADMINISTRAÇÃO · SUITE HELP</p>
          <h1>Editor da Suite Help</h1>
          <p className="muted">Gerencie ordens de serviço, prazos e orientações. Nada chega à operação antes da publicação do Administrador.</p>
        </div>
        <div className="heading-actions">
          {draftRelease && <span className="status-chip warning">Rascunho {draftRelease.version}</span>}
          <button className="secondary-button compact" disabled={saving} onClick={() => { setBulkOpen((current) => !current); setFormOpen(false) }}>Importar em massa</button>
          <button className="primary-button compact" disabled={saving} onClick={openNew}>+ Nova OS</button>
        </div>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      <div className="editor-summary">
        <article><span>Base oficial</span><strong>{records.length}</strong><small>registros publicados</small></article>
        <article><span>Alterações pendentes</span><strong>{drafts.length}</strong><small>{draftRelease?.version ?? 'sem rascunho ativo'}</small></article>
        <article><span>Publicação</span><strong>{profile?.role === 'administrador' ? 'Liberada' : 'Somente Admin'}</strong><small>Gestão prepara; Admin publica</small></article>
      </div>

      <section className="bulk-operations-bar">
        <div>
          <strong>Operações em massa</strong>
          <span>Para substituir toda a Suite Help: prepare a exclusão total, importe o novo CSV e publique tudo na mesma versão.</span>
        </div>
        <div className="bulk-operations-actions">
          <button className="secondary-button compact" disabled={saving} onClick={downloadCsvTemplate}>Baixar modelo CSV</button>
          <button className="danger-button compact" disabled={saving || records.length === 0} onClick={() => void markDeleteAll()}>Excluir toda a base</button>
        </div>
      </section>

      {bulkOpen && (
        <section className="editor-form-card bulk-import-card">
          <div className="pending-head">
            <div>
              <p className="eyebrow">IMPORTAÇÃO EM MASSA</p>
              <h2>Preparar várias ordens de serviço</h2>
              <p className="muted">Aceita CSV separado por ponto e vírgula ou vírgula, inclusive campos entre aspas com quebra de linha.</p>
            </div>
            <button className="text-button" onClick={() => setBulkOpen(false)}>Fechar</button>
          </div>
          <div className="bulk-file-row">
            <label className="file-picker">Selecionar CSV<input type="file" accept=".csv,text/csv" onChange={(event) => void readBulkFile(event)} /></label>
            <span>{bulkFileName || 'Nenhum arquivo selecionado'}</span>
          </div>
          <textarea className="bulk-textarea" value={bulkText} onChange={(event) => setBulkText(event.target.value)} placeholder="Você também pode colar aqui o conteúdo CSV..." rows={10} />
          <div className="notice info compact-note">A chave interna é gerada automaticamente como OS- + número da ordem de serviço. Em palavras-chave, separe termos por | ou vírgula.</div>
          <div className="form-actions">
            <button className="secondary-button" type="button" onClick={downloadCsvTemplate}>Baixar modelo</button>
            <button className="primary-button compact" type="button" disabled={saving || !bulkText.trim()} onClick={() => void prepareBulkImport()}>{saving ? 'Preparando...' : 'Preparar importação'}</button>
          </div>
        </section>
      )}

      {drafts.length > 0 && (
        <section className="pending-panel">
          <div className="pending-head">
            <div><p className="eyebrow">RASCUNHO</p><h2>Alterações aguardando publicação</h2></div>
            <button className="primary-button compact" disabled={saving || profile?.role !== 'administrador'} onClick={() => void publish()}>Publicar {draftRelease?.version}</button>
          </div>
          <div className="pending-list">
            {drafts.map((draft) => (
              <div className="pending-item" key={draft.id}>
                <span className={`operation-badge ${draft.operacao.toLowerCase()}`}>{draft.operacao}</span>
                <div>
                  <strong>{String(draft.dados_novos?.ordem_servico ?? draft.dados_anteriores?.ordem_servico ?? draft.record_key)} · {String(draft.dados_novos?.assunto ?? draft.dados_anteriores?.assunto ?? '')}</strong>
                  <small>Alteração preparada</small>
                </div>
                <button className="danger-link" disabled={saving} onClick={() => void discardDraft(draft)}>Descartar</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {formOpen && (
        <section className="editor-form-card">
          <div className="pending-head">
            <div>
              <p className="eyebrow">{editing ? 'EDITAR' : 'NOVA ORDEM DE SERVIÇO'}</p>
              <h2>{editing ? `${editing.ordem_servico} · ${editing.assunto}` : 'Cadastrar OS'}</h2>
            </div>
            <button className="text-button" onClick={closeForm}>Fechar</button>
          </div>
          <form className="editor-form" onSubmit={saveDraft}>
            <label>Ordem de Serviço<input value={form.ordem_servico} onChange={(event) => updateField('ordem_servico', event.target.value)} placeholder="Ex.: 102004" required /></label>
            <label>Assunto<input value={form.assunto} onChange={(event) => updateField('assunto', event.target.value)} required /></label>
            <label>Custo<input value={form.custo} onChange={(event) => updateField('custo', event.target.value)} /></label>
            <label>Prazo<input value={form.prazo} onChange={(event) => updateField('prazo', event.target.value)} /></label>
            <label>Quem pode solicitar<input value={form.quem_pode_solicitar} onChange={(event) => updateField('quem_pode_solicitar', event.target.value)} /></label>
            <label>Canal de solicitação<input value={form.canal_solicitacao} onChange={(event) => updateField('canal_solicitacao', event.target.value)} /></label>
            <label className="span-2">Quando abrir<textarea rows={5} value={form.quando_abrir} onChange={(event) => updateField('quando_abrir', event.target.value)} /></label>
            <label className="span-2">Sugestão de texto<textarea rows={6} value={form.sugestao_texto} onChange={(event) => updateField('sugestao_texto', event.target.value)} /></label>
            <label className="span-2">Observação IA<textarea rows={5} value={form.observacao_ia} onChange={(event) => updateField('observacao_ia', event.target.value)} /></label>
            <label className="span-2">Palavras-chave<input value={form.palavras_chave} onChange={(event) => updateField('palavras_chave', event.target.value)} placeholder="Separe por | ou vírgula" /></label>
            <div className="form-actions span-2">
              <button type="button" className="secondary-button" onClick={closeForm}>Cancelar</button>
              <button type="submit" className="primary-button compact" disabled={saving}>{saving ? 'Salvando...' : 'Salvar em rascunho'}</button>
            </div>
          </form>
        </section>
      )}

      <div className="admin-table-toolbar">
        <div><h2>Base oficial</h2><p className="muted">{filtered.length} de {records.length} registros</p></div>
        <input aria-label="Pesquisar Suite Help" placeholder="Pesquisar OS, assunto, prazo ou orientação..." value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>

      {loading ? <div className="empty-state">Carregando Suite Help...</div> : (
        <div className="admin-table-wrap">
          <table className="admin-data-table suite-help-table">
            <thead><tr><th>OS</th><th>Assunto</th><th>Custo</th><th>Prazo</th><th>Quem pode solicitar</th><th>Canal</th><th>Ações</th></tr></thead>
            <tbody>
              {filtered.map((record) => {
                const pending = pendingByKey.get(record.codigo)
                return (
                  <tr key={record.id} className={pending ? 'has-pending' : ''}>
                    <td><strong>{record.ordem_servico}</strong>{pending && <span className="row-pending">{pending.operacao} pendente</span>}</td>
                    <td>{record.assunto}</td>
                    <td>{record.custo || '—'}</td>
                    <td>{record.prazo || '—'}</td>
                    <td>{record.quem_pode_solicitar || '—'}</td>
                    <td>{record.canal_solicitacao || '—'}</td>
                    <td><div className="table-actions"><button onClick={() => openEdit(record)}>Editar</button><button className="danger-link" onClick={() => void markDelete(record)}>Excluir</button></div></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
